import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { EmptyState } from "@/components/EmptyState";
import { TableSkeleton } from "@/components/Skeletons";
import { Wallet, RefreshCw, AlertTriangle, Link2, Search, ChevronDown, Check, Ban, Undo2 } from "lucide-react";

const daysAgo = (dateStr) => {
  if (!dateStr) return null;
  const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return d >= 0 ? d : null;
};

/** Lightweight searchable combobox — no native <select>, since Zoho contact lists run into
 * the hundreds and NJM's client list runs ~100+; a plain dropdown is unusable at that size.
 * The options panel renders in a document.body PORTAL with `position: fixed` coordinates
 * computed from the trigger button — so it's never clipped by an ancestor's `overflow-hidden`
 * (e.g. the surrounding <section>/<table>) and always sits above everything else (z-[9999)]. */
function SearchableSelect({ options, value, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const selected = options.find((o) => o.value === value);

  const reposition = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 256) });
  };

  useEffect(() => {
    if (!open) return;
    reposition();
    const onScrollOrResize = () => reposition();
    const onClickOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("mousedown", onClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = (query ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase())) : options).slice(0, 50);

  const pick = (val) => { onChange(val); setOpen(false); setQuery(""); };

  return (
    <div className="relative">
      <button type="button" ref={triggerRef} data-testid={testId} onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2 py-1.5 text-xs hover:border-[#FFC817]/40">
        <span className={`truncate ${selected ? "text-[#1C1C1E]" : "text-[#A8A29E]"}`}>{selected?.label || placeholder}</span>
        <ChevronDown className="h-3 w-3 text-[#A8A29E] shrink-0" />
      </button>
      {open && coords && createPortal(
        <div ref={panelRef} data-testid={`${testId}-panel`} style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, zIndex: 9999 }}
          className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] shadow-2xl">
          <div className="flex items-center gap-1.5 border-b border-[#E7E5E4] px-2 py-1.5">
            <Search className="h-3 w-3 text-[#A8A29E] shrink-0" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} data-testid={`${testId}-search`}
              placeholder="Search…" className="flex-1 bg-transparent text-xs text-[#1C1C1E] outline-none placeholder-[#A8A29E]" />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            <button type="button" onClick={() => pick("")} className="w-full text-left px-2 py-1.5 text-xs text-[#A8A29E] hover:bg-[#F5F5F4]">— none —</button>
            {filtered.length === 0 && <div className="px-2 py-3 text-center text-xs text-[#A8A29E]">No matches</div>}
            {filtered.map((o) => (
              <button key={o.value} type="button" data-testid={`${testId}-option-${o.value}`} onClick={() => pick(o.value)}
                className={`w-full text-left px-2 py-1.5 text-xs truncate hover:bg-[#F5F5F4] ${o.value === value ? "text-[#B45309]" : "text-[#1C1C1E]"}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default function Finance() {
  const { hasScope } = useAuth();
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [matches, setMatches] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [zohoError, setZohoError] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [showAllMatches, setShowAllMatches] = useState(false);
  const [excludeTarget, setExcludeTarget] = useState(null); // {client_id, client_name} while the exclude modal is open
  const [excludeReason, setExcludeReason] = useState("");
  const [excludeNote, setExcludeNote] = useState("");
  const [busyIds, setBusyIds] = useState(new Set());

  const load = async () => {
    try {
      const [s, sum, m] = await Promise.all([
        api.get("/v1/accounting/status"),
        api.get("/v1/accounting/receivables"),
        api.get("/v1/accounting/matches"),
      ]);
      setStatus(s.data); setSummary(sum.data); setMatches(m.data);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    }
  };
  useEffect(() => { load(); }, []);

  const doRefresh = async () => {
    setRefreshing(true);
    setZohoError(null);
    try {
      const r = await api.post("/v1/accounting/sync/refresh");
      const { matching } = r.data;
      toast.success(`Receivables refreshed — ${matching.suggested} suggested, ${matching.unmatched} unmatched, ${matching.ambiguous} ambiguous`);
      load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const errMsg = e?.response?.data?.detail?.error;
      if (e?.response?.status === 503) toast.error("Zoho isn't connected yet — ask the founder to connect it from Administration");
      else if (code === "zoho_invalid_client") { setZohoError(errMsg); toast.error("Zoho connection needs attention — see banner below"); }
      else toast.error(errMsg ? `Couldn't refresh receivables: ${errMsg}` : "Couldn't refresh receivables");
    } finally {
      setRefreshing(false);
    }
  };

  const setMatch = async (clientId, zohoContactId) => {
    try {
      await api.post(`/v1/accounting/matches/${clientId}`, { zoho_contact_id: zohoContactId || null });
      toast.success("Mapping updated");
      load();
    } catch {
      toast.error("Couldn't update mapping");
    }
  };

  const withBusy = async (id, fn) => {
    setBusyIds((prev) => new Set(prev).add(id));
    try { await fn(); } finally { setBusyIds((prev) => { const n = new Set(prev); n.delete(id); return n; }); }
  };

  const confirmOne = (clientId) => withBusy(clientId, async () => {
    try {
      await api.post("/v1/accounting/matches/confirm", { client_ids: [clientId] });
      toast.success("Match confirmed");
      load();
    } catch {
      toast.error("Couldn't confirm match");
    }
  });

  const confirmAllSuggested = async () => {
    const suggestedCount = (matches?.needs_matching || []).filter((m) => m.match_type === "suggested").length;
    if (!suggestedCount) return;
    if (!window.confirm(`Confirm all ${suggestedCount} suggested matches?`)) return;
    try {
      const r = await api.post("/v1/accounting/matches/confirm", { confirm_all: true });
      toast.success(`${r.data.confirmed.length} match(es) confirmed`);
      load();
    } catch {
      toast.error("Couldn't bulk-confirm matches");
    }
  };

  const openExclude = (item) => { setExcludeTarget(item); setExcludeReason(""); setExcludeNote(""); };
  const submitExclude = async () => {
    if (!excludeReason) return;
    await withBusy(excludeTarget.client_id, async () => {
      try {
        await api.post(`/v1/accounting/matches/${excludeTarget.client_id}/exclude`, { reason: excludeReason, note: excludeNote || undefined });
        toast.success(`${excludeTarget.client_name} excluded`);
        setExcludeTarget(null);
        load();
      } catch {
        toast.error("Couldn't exclude client");
      }
    });
  };
  const unexclude = (item) => withBusy(item.client_id, async () => {
    try {
      await api.post(`/v1/accounting/matches/${item.client_id}/unexclude`);
      toast.success(`${item.client_name} restored to matching`);
      load();
    } catch {
      toast.error("Couldn't restore client");
    }
  });

  if (forbidden) return <div className="p-8 text-sm text-[#A8A29E]" data-testid="finance-forbidden">Access Restricted</div>;
  if (!status) return <div className="p-8" data-testid="finance-skeleton"><TableSkeleton cols={6} testId="finance-loading-skeleton" /></div>;

  const clientOptions = (matches?.items || []).map((it) => ({ value: it.client_id, label: it.client_name }));
  const zohoOptions = (matches?.zoho_contacts || []).map((z) => ({ value: z.contact_id, label: z.contact_name }));
  const displayList = showAllMatches ? (matches?.items || []) : (matches?.needs_matching || []);
  const visibleItems = clientSearch
    ? displayList.filter((m) => (m.client_name || "").toLowerCase().includes(clientSearch.toLowerCase()))
    : displayList;
  const suggestedCount = (matches?.needs_matching || []).filter((m) => m.match_type === "suggested").length;

  return (
    <div className="p-8 space-y-6" data-testid="finance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-editorial text-4xl tracking-tight flex items-center gap-2"><Wallet className="h-7 w-7 text-[#B45309]" /> Receivables</h1>
          <p className="text-sm text-[#57534E] mt-1">Live figures mirrored from Zoho Books (India) — read-only.</p>
        </div>
        {hasScope("accounting.read") && (
          <button data-testid="refresh-receivables-btn" onClick={doRefresh} disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Refreshing…" : "Refresh Receivables"}
          </button>
        )}
      </div>

      {!status.connected && (
        <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-4 text-xs text-orange-600" data-testid="zoho-not-connected-banner">
          Zoho Books isn't connected yet — ask the founder to connect it from Administration.
        </div>
      )}

      {zohoError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-xs text-red-600 flex items-start gap-2" data-testid="zoho-invalid-client-banner">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{zohoError}</span>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Stat label="Total Outstanding" value={formatMoney(summary.total_outstanding, summary.currency)} testId="total-outstanding-stat" />
          <Stat label="Overdue Invoices" value={summary.overdue_count} testId="overdue-count-stat" />
        </div>
      )}

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] overflow-hidden">
        <header className="border-b border-[#E7E5E4] px-4 py-3 text-xs uppercase tracking-wider text-[#A8A29E] flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" /> Overdue Invoices (sorted oldest first)
        </header>
        {!summary || summary.overdue_invoices.length === 0 ? (
          <EmptyState title="No overdue invoices" hint="Everything's currently within terms." testId="overdue-invoices-empty" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E7E5E4] text-xs uppercase tracking-wider text-[#A8A29E]">
                <th className="text-left px-4 py-2 font-medium">Client</th>
                <th className="text-left px-4 py-2 font-medium">Invoice #</th>
                <th className="text-left px-4 py-2 font-medium">Date</th>
                <th className="text-right px-4 py-2 font-medium">Total</th>
                <th className="text-right px-4 py-2 font-medium">Balance</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Age</th>
              </tr>
            </thead>
            <tbody>
              {summary.overdue_invoices.map((inv) => (
                <tr key={inv.invoice_id} data-testid={`overdue-invoice-${inv.invoice_id}`} className="border-b border-[#E7E5E4] hover:bg-[#F5F5F4]">
                  <td className="px-4 py-2 text-[#1C1C1E]">{inv.njm_client_name || inv.customer_name || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-[#57534E]">{inv.invoice_number}</td>
                  <td className="px-4 py-2 text-xs text-[#57534E]">{inv.date}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-[#57534E]">{formatMoney(inv.total, inv.currency_code)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-[#1C1C1E]">{formatMoney(inv.balance, inv.currency_code)}</td>
                  <td className="px-4 py-2"><span className="rounded-full bg-[#E7E5E4] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#57534E]">{inv.status}</span></td>
                  <td className="px-4 py-2 text-right text-xs text-orange-600">{daysAgo(inv.date)}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-lg border border-orange-500/30 bg-orange-500/5 overflow-hidden" data-testid="priority-unmatched-section">
        <header className="border-b border-orange-500/20 px-4 py-3 text-xs uppercase tracking-wider text-orange-600 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" /> Zoho customers with outstanding invoices not yet linked to a client
        </header>
        {!matches || matches.priority_unmatched.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-[#A8A29E]">All outstanding balances are linked to an NJM client</div>
        ) : (
          <div className="divide-y divide-orange-500/10">
            {matches.priority_unmatched.map((z) => (
              <div key={z.contact_id} data-testid={`priority-unmatched-${z.contact_id}`} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm text-[#1C1C1E] truncate">{z.contact_name}</div>
                  <div className="text-xs text-orange-600">{formatMoney(z.outstanding_receivable_amount, z.currency_code)} outstanding</div>
                </div>
                <div className="w-64 shrink-0">
                  <SearchableSelect
                    testId={`priority-link-select-${z.contact_id}`}
                    options={clientOptions}
                    value=""
                    placeholder="Link to NJM client…"
                    onChange={(clientId) => clientId && setMatch(clientId, z.contact_id)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] overflow-hidden">
        <header className="border-b border-[#E7E5E4] px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E] flex items-center gap-1.5">
            <Link2 className="h-3.5 w-3.5" /> Client ↔ Zoho Matching
            {!showAllMatches && <span className="normal-case text-[#57534E]">— Needs Matching ({(matches?.needs_matching || []).length})</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {suggestedCount > 0 && (
              <button data-testid="confirm-all-suggested-btn" onClick={confirmAllSuggested}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-1.5 text-xs text-emerald-700 hover:bg-emerald-500/10">
                Confirm all {suggestedCount} suggested
              </button>
            )}
            <button data-testid="matches-view-toggle" onClick={() => setShowAllMatches((v) => !v)}
              className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">
              {showAllMatches ? "Show Needs Matching only" : "Show all"}
            </button>
            <div className="flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2 py-1">
              <Search className="h-3 w-3 text-[#A8A29E]" />
              <input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} data-testid="client-match-search"
                placeholder="Search clients…" className="bg-transparent text-xs text-[#1C1C1E] outline-none placeholder-[#A8A29E] w-40" />
            </div>
          </div>
        </header>
        {!matches || displayList.length === 0 ? (
          <EmptyState title={showAllMatches ? "No clients to match yet" : "Needs Matching is clear"} hint={showAllMatches ? undefined : "Every client is either matched or excluded."} testId="client-match-empty" />
        ) : visibleItems.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-[#A8A29E]">No clients match "{clientSearch}"</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E7E5E4] text-xs uppercase tracking-wider text-[#A8A29E]">
                <th className="text-left px-4 py-2 font-medium">NJM Client</th>
                <th className="text-left px-4 py-2 font-medium">Match Status</th>
                <th className="text-right px-4 py-2 font-medium">Balance</th>
                <th className="text-left px-4 py-2 font-medium">Zoho Contact</th>
                <th className="text-right px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((m) => (
                <tr key={m.client_id} data-testid={`match-row-${m.client_id}`} className="border-b border-[#E7E5E4] hover:bg-[#F5F5F4]">
                  <td className="px-4 py-2 text-[#1C1C1E]">{m.client_name}</td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                      m.match_type === "auto" ? "bg-emerald-500/10 text-emerald-600" :
                      m.match_type === "manual" ? "bg-[#FFC817]/10 text-[#B45309]" :
                      m.match_type === "suggested" ? "bg-blue-500/10 text-blue-600" :
                      m.match_type === "excluded" ? "bg-[#E7E5E4] text-[#78716C]" :
                      m.match_type === "ambiguous" ? "bg-orange-500/10 text-orange-600" : "bg-red-500/10 text-red-600"
                    }`} data-testid={`match-status-${m.client_id}`}>{m.match_type}</span>
                    {m.match_type === "excluded" && m.excluded_reason && (
                      <div className="text-[10px] text-[#A8A29E] mt-0.5" data-testid={`excluded-reason-${m.client_id}`}>{m.excluded_reason}{m.excluded_note ? ` — ${m.excluded_note}` : ""}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-[#57534E]" data-testid={`indicative-balance-${m.client_id}`}>
                    {m.indicative_balance > 0 ? formatMoney(m.indicative_balance, m.candidates?.[0]?.currency_code || "INR") : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <div className="w-64">
                      <SearchableSelect
                        testId={`match-select-${m.client_id}`}
                        options={zohoOptions}
                        value={m.zoho_contact_id || ""}
                        placeholder="— none —"
                        onChange={(contactId) => setMatch(m.client_id, contactId)}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {m.match_type === "suggested" && (
                      <button data-testid={`confirm-match-btn-${m.client_id}`} disabled={busyIds.has(m.client_id)} onClick={() => confirmOne(m.client_id)}
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2 py-1 text-[11px] text-emerald-700 hover:bg-emerald-500/10 disabled:opacity-50 mr-1.5">
                        <Check className="h-3 w-3" /> Confirm
                      </button>
                    )}
                    {m.match_type === "excluded" ? (
                      <button data-testid={`unexclude-btn-${m.client_id}`} disabled={busyIds.has(m.client_id)} onClick={() => unexclude(m)}
                        className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] px-2 py-1 text-[11px] text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
                        <Undo2 className="h-3 w-3" /> Restore
                      </button>
                    ) : (
                      <button data-testid={`exclude-btn-${m.client_id}`} disabled={busyIds.has(m.client_id)} onClick={() => openExclude(m)}
                        className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] px-2 py-1 text-[11px] text-[#57534E] hover:border-red-400 hover:text-red-600 disabled:opacity-50">
                        <Ban className="h-3 w-3" /> Exclude
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {excludeTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="exclude-modal">
          <div className="w-full max-w-sm rounded-lg bg-[#FFFFFF] p-5 shadow-2xl">
            <h3 className="text-sm text-[#1C1C1E] mb-1">Exclude "{excludeTarget.client_name}"</h3>
            <p className="text-xs text-[#A8A29E] mb-3">Removes this client from Needs Matching without requiring a Zoho link.</p>
            <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Reason</label>
            <select value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)} data-testid="exclude-reason-select"
              className="mt-1 mb-3 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2 py-1.5 text-xs text-[#1C1C1E]">
              <option value="">— select a reason —</option>
              {(matches?.exclude_reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            {excludeReason === "Other" && (
              <textarea value={excludeNote} onChange={(e) => setExcludeNote(e.target.value)} data-testid="exclude-note-input"
                placeholder="Optional note…" className="mb-3 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2 py-1.5 text-xs text-[#1C1C1E] placeholder:text-[#A8A29E]" rows={2} />
            )}
            <div className="flex justify-end gap-2">
              <button data-testid="exclude-cancel-btn" onClick={() => setExcludeTarget(null)} className="rounded-md px-3 py-1.5 text-xs text-[#57534E] hover:text-[#1C1C1E]">Cancel</button>
              <button data-testid="exclude-confirm-btn" onClick={submitExclude} disabled={!excludeReason || busyIds.has(excludeTarget.client_id)}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">Exclude</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-4" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">{label}</div>
    <div className="text-xl mt-1 font-mono">{value}</div>
  </div>
);
